/**
 * Token name constants — single source of truth for runtime theming.
 *
 * These arrays list the BRAND-DEPENDENT SUBSET of the SCSS maps in:
 *   - radiant-styles/css/foundations/_tokens-system.scss   ($rd-sys-color-map)
 *   - radiant-styles/css/foundations/_tokens-component*.scss ($rd-comp-color-map)
 *
 * Fixed-semantic tokens (success/warning/failure/information, accent-*,
 * attribute/measure/date/error chips, shadows, progress, segmented-control,
 * charts) are intentionally absent: the theme generator does not emit them,
 * so the static SCSS light/dark declarations always apply.
 *
 * Names here must match the SCSS map keys exactly — a mismatched name
 * injects a CSS variable nothing reads while the real token keeps its
 * static value. theme-generator.spec.ts pins generated keys to these arrays.
 *
 * Renaming/removing entries is safe for cleanup: clearTheme() also does a
 * prefix sweep over all inline custom properties.
 */

// =========================================================================
// System token names (24 tokens)
// =========================================================================

export const SYSTEM_TOKEN_NAMES = [
    // Background (12)
    'background-sunken',
    'background-base',
    'background-on-base',
    'background-raised',
    'background-active',
    'background-secondary-action',
    'background-secondary-hover',
    'background-brand',
    'background-overlay',
    'background-inverse',
    'background-ghost-hover',
    'background-ghost-highlight',

    // Content (5)
    'content-primary',
    'content-secondary',
    'content-tertiary',
    'content-brand',
    'content-inverse',

    // Border (7)
    'border-bold',
    'border-bold-hover',
    'border-subtle',
    'border-subtle-hover',
    'border-brand',
    'border-focus',
    'border-gap',
] as const;

// =========================================================================
// Component token names (29 SCSS-backed + 2 generator-only)
// =========================================================================

export const COMPONENT_TOKEN_NAMES = [
    // Button
    'button-primary-default',
    'button-primary-hover',
    'button-primary-pressed',
    'button-primary-on-color-default',
    'button-primary-on-color-hover',
    'button-primary-on-color-pressed',
    'button-secondary-default',
    'button-secondary-hover',
    'button-secondary-pressed',
    'button-tertiary-hover',
    'button-tertiary-pressed',
    'button-bg-overlay',

    // Generator-only (no SCSS map entry):
    //   button-primary-content  — contrast-picked CTA text; bridged to
    //                             --ts-var-button--primary-color
    //   button-primary-disabled — consumed by the disabled rule in
    //                             radiant-react button.module.scss
    'button-primary-content',
    'button-primary-disabled',

    // Chip – Loading
    'chip-loading-default',

    // Chip – Filter (neutral)
    'chip-filter-default',
    'chip-filter-hover',
    'chip-filter-pressed',
    'chip-filter-icon',
    'chip-filter-active-border',

    // Chip – Input (neutral)
    'chip-input-default',
    'chip-input-hover',
    'chip-input-pressed',
    'chip-input-selected-default',
    'chip-input-selected-hover',
    'chip-input-selected-pressed',

    // Toggle
    'toggle-track-off',
    'toggle-track-on',
    'toggle-thumb-off',
    'toggle-thumb-on',
    'toggle-thumb-outline-off',
] as const;

// =========================================================================
// CSS variable prefixes — used when injecting / clearing tokens on :root
// =========================================================================

export const SYS_TOKEN_PREFIX = '--rd-sys-color-';
export const COMP_TOKEN_PREFIX = '--rd-comp-color-';

// =========================================================================
// Derived types
// =========================================================================

export type SystemTokenName = typeof SYSTEM_TOKEN_NAMES[number];
export type ComponentTokenName = typeof COMPONENT_TOKEN_NAMES[number];
